<template>
    <div class="col-sm-12 editor-form" >
      <h2 class="editor-header">{{ $t('work_time.add_record') }}</h2>
      <div class="col-sm-12 form-data nopads">
        <!-- Users -->
        <div
          v-if="!userId"
          class="nopads"
        >
          <span class="span-title">{{ $t('work_time.user') }}</span>
          <select
            id="user"
            v-model="user"
            class="form-select form-select-sm mb-1"
            :disabled="!users || users.length < 1"
          >
              <option
                :value="null"
                disabled
              >
                {{ $t('work_time.select_user') }}
              </option>
            <option
              v-for="item in users"
              :key="item.id"
              :value="item.id"
            >
              {{ item.last_name }}, {{ item.first_name }}
            </option>
          </select>
        </div>
        <div v-else>
          <h3>
            {{ name }}
          </h3>
          <span class="span-title">{{ timeRange }}</span>
        </div>

        <div class="row nopads">
          <!-- Work time modes -->
          <div class="col-sm-6 nopads">
            <span class="span-title">{{ $t('work_time.mode') }}</span>
            <select
              id="mode"
              v-model="workTimeMode"
              class="form-select form-select-sm mb-1"
              :disabled="!workTimeModes || workTimeModes.length < 1"
            >
                <option
                  :value="null"
                  disabled
                >
                  {{ $t('work_time.select_mode') }}
                </option>
              <option
                v-for="item in workTimeModes"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </option>
            </select>
          </div>
          <div class="col-sm-6 nopads pl-2">
            <!-- Cost centers -->
            <span class="span-title">{{ $t('work_time.cost_center') }}</span>
            <select
              id="costCenter"
              v-model="costCenter"
              class="form-select form-select-sm mb-1"
              :disabled="!costCenters || costCenters.length < 1"
            >
                <option
                  :value="null"
                  disabled
                >
                  {{ $t('work_time.select_cost_center') }}
                </option>
              <option
                v-for="item in costCenters"
                :key="item.id"
                :value="item.id"
              >
                {{ item.name }}
              </option>
            </select>
          </div>
        </div>

        <!-- Start time -->
        <div class="row nopads">
          <div class="col-sm-6 nopads">
            <span class="span-title">{{ $t('work_time.start') }}</span>
            <date-time-picker
              id="start"
              v-model:date="start"
              class="datetime-routa"
              format="dd.MM.yyyy HH:mm"
              :enableTimePicker="true"
              :placeholder="$t('observations_videos.search_input_format')"
            />
          </div>

          <!-- End time -->
          <div class="col-sm-6 nopads pl-2">
            <span class="span-title">{{ $t('work_time.end') }}</span>
            <date-time-picker
              id="end"
              v-model:date="end"
              class="datetime-routa"
              format="dd.MM.yyyy HH:mm"
              :enableTimePicker="true"
              :placeholder="$t('observations_videos.search_input_format')"
            />
          </div>
        </div>
        <div class="row nopads">
          <div class="col-sm-6 nopads">
            <span class="span-title">{{ $t('work_shift.shift') }}</span>
            <select
              v-model="shift"
              class="form-select form-select-sm no-margin"
            >
                    <option
                        :value="null"
                        disabled
                    >
                        {{ $t('common.select') }}
                    </option>
                <option
                    v-for="item in shifts"
                    :key="item.value"
                    :value="item.value"
                >
                    {{ item.text }}
                </option>
            </select>
          </div>
          <!-- Vehicle -->
          <div class="col-sm-6 nopads pl-2">
            <span class="span-title">{{ $t('trip_list.vehicle') }}</span>
            <select
              id="mode"
              v-model="vehicle"
              class="form-select form-select-sm mb-1"
              :disabled="!vehicles || vehicles.length < 1"
            >
                <option
                  :value="null"
                  disabled
                >
                  {{ $t('work_time.select_vehicle') }}
                </option>
              <option
                v-for="item in vehicles"
                :key="item.id"
                :value="item.id"
              >
                {{ item.make }} {{ item.vehicle_model }} - {{ item.license_plate }}
              </option>
            </select>
          </div>
        </div>

        <!-- Contract -->
        <open-order-filter
          :to="start"
          :from="end"
          v-model="order"
          @loading="setLoading"
          @loading-completed="loadingDone"
        />

        <!-- Note -->
        <div class="col-sm-12 nopads">
          <span class="span-title">{{ $t('work_time.note') }}</span>
          <textarea
            id="info"
            v-model="note"
            class="form-control"
            :placeholder="$t('work_time.note')"
            :rows="4"
          />
        </div>

        <div class="col-sm-12 button-container">
          <button
            class="btn btn-secondary form-button"
            @click.stop="$emit('closeAndUpdate')"
          >
            {{ $t('common.cancel') }}
          </button>
          <button
            class="btn btn-success form-button"
            :disabled="loading"
            @click.stop="submit"
          >
            {{ $t('common.save') }}
          </button>
        </div>
      </div>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {workManagementHelper} from '../mixins/WorkManagementMixin'
import {restApi} from '../mixins/RestApiMixin'
import OpenOrderFilter from "../order/OpenOrderFilter";
import DateTimePicker from "@/components/common/DateTimePicker";
import {inject} from "vue";

export default {
    name: 'WorkTimeAdd',
    components: {OpenOrderFilter, DateTimePicker},
    mixins: [workManagementHelper, restApi],
    props: {
        name: {
            type: String,
            default() {
                return ''
            }
        },
        userId: {
            type: Number,
            default: -1
        },
        timeRange: {
            type: String,
            default() {
                return ''
            }
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            workTimeMode: null,
            workTimeModes: [],
            user: this.userId,
            users: [],
            record: this.newWorkTimeRecord(),
            start: null,
            end: null,
            costCenter: null,
            costCenters: [],
            order: null,
            note: '',
            shift: null,
            shifts: [
                {value: null, text: this.$t('work_shift.select_shift')},
                {value: 1, text: this.$t('work_shift.day_shift')},
                {value: 2, text: this.$t('work_shift.morning_shift')},
                {value: 3, text: this.$t('work_shift.evening_shift')},
                {value: 4, text: this.$t('work_shift.night_shift')}
            ],
            vehicles: [],
            vehicle: null
        }
    },

    mounted: function () {
        this.initCompanyData()
    },

    methods: {
        initCompanyData: function () {
            this.fetchUsers()
            this.fetchWorkTimeModes()
            this.fetchCostCenters()
            this.fetchVehicles()
        },

        fetchUsers: function () {
            this.restFetch(this.userUrl, this.handleUsers)
        },

        handleUsers: function (response) {
            this.users = response.data
            this.loading = false
        },

        fetchWorkTimeModes: function () {
             this.restFetch(this.workTimeModeUrl, this.handleWorkTimeModes)
        },

        handleWorkTimeModes: function (response) {
            this.workTimeModes = response.data
            this.loading = false
        },

        fetchCostCenters: function () {
             this.restFetch(this.costCenterUrl, this.handleCostCenters)
        },

        handleCostCenters: function (response) {
            this.costCenters = response.data
            this.loading = false
        },

        fetchVehicles: function () {
            this.loading = true
            this.vehicles = []
            this.vehicle = null
            this.restFetch(this.vehicleUrl, this.handleVehicles)
        },

        handleVehicles: function (response) {
            if (response && response.data) {
                this.vehicles = response.data
            }
            this.loading = false
        },

        setValues: function () {
            let item = this.newWorkTimeRecord()
            if (this.workTimeMode) {
                item.mode = {id: this.workTimeMode}
            } else {
                this.showAlert(this.$t('work_time.error_mode_missing'))
                return
            }
            if (this.user) {
                item.user = {id: this.user}
            } else {
                this.showAlert(this.$t('work_time.error_user_missing'))
                return
            }
            if (this.start) {
                item.start_time = this.start
            } else {
                this.showAlert(this.$t('work_time.error_start_missing'))
                return
            }
            if (this.end) {
                if(!this.isValidEndTime(this.start, this.end)) {
                    this.showAlert(this.$t('work_time.invalid_end_time'))
                    return
                }
                item.end_time = this.end
            }
            if (this.costCenter) {
                item.cost_center = {id: this.costCenter}
            }
            if (this.order) {
                item.order = {id: this.order.id}
            }
            if (this.shift) {
                item.shift = this.shift
            }
            if (this.vehicle) {
                item.vehicle = {id: this.vehicle}
            }
            item.note = this.note
            if (this.isWorkType() && !this.costCenter) {
                // Give error if work type mode is selected without cost center!
                this.showAlert(this.$t('work_time.error_cost_center_missing'))
                return
            }
            return item
        },

        submit: function () {
            let json = this.setValues(this.record)
            if (json !== undefined) {
                this.loading = true
                this.restAdd(this.workTimeUserUrl, json, this.success, this.fail)
            }
        },

        success: function () {
            this.$emit('closeAndUpdate')
            this.loading = false
        },

        fail: function (response) {
            this.loading = false
            if (response.status === 406) {
                this.showAlert(this.$t('work_time.error_overlapping'))
            } else {
                this.showAlert(this.$t('common.alert_update_failed'))
            }
        },

        isValidEndTime: function (startTime, endTime) {
            return new Date(startTime).getTime() < new Date(endTime).getTime()
        },

        isWorkType: function () {
            if (this.workTimeMode) {
                let foundMode = this.workTimeModes.find(function (item) {
                    return item.id === this.workTimeMode
                }, this)
                return foundMode && foundMode.work_type && foundMode.work_type === true
            }
            return false
        },

        setOrder: function (order) {
            this.order = order
        },

        setLoading: function () {
            this.loading = true
        },

        loadingDone: function () {
            this.loading = false
        }
    }
}
</script>
